import type { ApiKey } from "@server/types/apiKeyTypes.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createApiKey, deleteApiKey } from "@/api/apiKey";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";
import { queryKeys } from "@/lib/query-keys";

export const useApiKeyActions = () => {
  const queryClient = useQueryClient();
  const organizationId = useRequiredOrgId();
  const apiKeysQueryKey = queryKeys.apiKeys.all(organizationId);

  const createKey = useMutation({
    mutationFn: createApiKey,
    onSettled: () => {
      // Refetch API keys after create completes
      void queryClient.invalidateQueries({ queryKey: apiKeysQueryKey });
    },
  });

  const deleteKey = useMutation({
    mutationFn: deleteApiKey,
    onMutate: async ({ keyId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: apiKeysQueryKey });

      // Snapshot the previous value
      const previousApiKeys =
        queryClient.getQueryData<ApiKey[]>(apiKeysQueryKey);

      // Optimistically update by removing the deleted api key
      if (previousApiKeys) {
        queryClient.setQueryData(
          apiKeysQueryKey,
          previousApiKeys.filter((key) => key.id !== keyId),
        );
      }

      // Return a context with the previous value
      return { previousApiKeys };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context from onMutate to roll back
      if (context?.previousApiKeys) {
        queryClient.setQueryData(apiKeysQueryKey, context.previousApiKeys);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure fresh data
      void queryClient.invalidateQueries({ queryKey: apiKeysQueryKey });
    },
  });

  return { createKey, deleteKey };
};
